import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, Row, Col, Form, Button, Table, Badge, ProgressBar, Alert, Pagination, Toast, ToastContainer, Modal, Spinner } from "react-bootstrap";
import Footer from "../layouts/Footer";
import * as XLSX from 'xlsx';
import { fetchSenderList } from "../services/api";

export default function EnvoieMasse() {
  // Constantes
  const MAX_ROWS = 100000;
  const MAX_FILE_SIZE = 50 * 1024 * 1024;
  const PREVIEW_ROWS_PER_PAGE = 10;
  const UPLOAD_TIMEOUT = 600000;
  const SIZE_THRESHOLD = 5 * 1024 * 1024;
  const ROWS_THRESHOLD = 10000;

  const [importStep, setImportStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState([]);
  const [fullFileData, setFullFileData] = useState([]);
  const [mapping, setMapping] = useState({});
  const [validationResults, setValidationResults] = useState({});
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('idle');
  const [importStats, setImportStats] = useState({});
  const [messageSource, setMessageSource] = useState('form');
  const token = localStorage.getItem('authToken');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [senderList, setSenderList] = useState([]);
  const [selectedSender, setSelectedSender] = useState("");

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('danger');

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    onConfirm: null,
    data: null
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);

  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadPhase, setUploadPhase] = useState('');

  const [splitMode, setSplitMode] = useState(false);
  const [part1Data, setPart1Data] = useState([]);
  const [part2Data, setPart2Data] = useState([]);
  const [currentPart, setCurrentPart] = useState(1);
  const [part1Result, setPart1Result] = useState(null);
  const [part2Result, setPart2Result] = useState(null);

  const summaryCardRef = useRef(null);
  const initDoneRef = useRef(false);

  const [messageData, setMessageData] = useState({
    texte: "",
    destinataires: "",
    type: "SMS Pro",
    reference: "",
    options: {
      accuséReception: false,
      messageLong: false,
      personnalisation: false
    }
  });

  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);

  const API_CONFIG = {
    proxyUrl: process.env.NODE_ENV === 'development'
      ? "/import-sms"
      : "https://api.minerve-tech.com/import-sms",
  };

  // ============================================
  // NOTIFICATIONS
  // ============================================
  const showNotification = (message, variant = 'danger') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 5000);
  };

  const showConfirmDialog = (title, message, onConfirm, data = null) => {
    setConfirmModalConfig({ title, message, onConfirm, data });
    setShowConfirmModal(true);
  };

  // ============================================
  // CHARGEMENT DES SENDERS
  // ============================================
  useEffect(() => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;

    const loadSenders = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('authToken');
        if (!token) {
          showNotification('Token d\'authentification manquant.', 'danger');
          throw new Error('Token d\'authentification manquant.');
        }

        const data = await fetchSenderList(token);

        if (data && Array.isArray(data)) {
          const formattedSenders = data.map(sender => ({
            value: sender.sender_id,
            label: sender.sender_id,
            status: sender.is_active
          }));

          setSenderList(formattedSenders);
          if (formattedSenders.length > 0) setSelectedSender(formattedSenders[0].value);
        } else {
          throw new Error('Format de données invalide');
        }
      } catch (err) {
        console.error('❌ Erreur senders:', err);
        setError(err.message);
        setSenderList([{ value: "MTECH241", label: "MTECH241", status: "YES" }]);
        setSelectedSender("MTECH241");
      } finally {
        setLoading(false);
      }
    };

    loadSenders();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [fullFileData]);

  // ============================================
  // DÉTECTION AUTO MAPPING
  // ============================================
  const detectAutoMapping = (headers) => {
    const autoMapping = {};
    headers.forEach((header, index) => {
      if (!header) return;
      const cleanHeader = String(header).toLowerCase().trim();

      if (autoMapping.msisdn === undefined) {
        if (cleanHeader === 'msisdn' || cleanHeader.includes('numero') ||
            cleanHeader.includes('téléphone') || cleanHeader.includes('phone') ||
            cleanHeader.includes('mobile')) {
          autoMapping.msisdn = index;
          return;
        }
      }
      if (autoMapping.message === undefined) {
        if (cleanHeader === 'message' || cleanHeader.includes('sms') ||
            cleanHeader.includes('text') || cleanHeader.includes('msg')) {
          autoMapping.message = index;
        }
      }
    });
    if (headers.length === 1 && autoMapping.msisdn === undefined) {
      autoMapping.msisdn = 0;
    }
    return autoMapping;
  };

  // ============================================
  // PAGINATION
  // ============================================
  const getCurrentPageData = () => {
    if (!fullFileData.length) return [];
    const startIndex = currentPage === 1 ? 0 : (currentPage - 1) * PREVIEW_ROWS_PER_PAGE;
    const endIndex = startIndex + PREVIEW_ROWS_PER_PAGE;
    return fullFileData.slice(startIndex, endIndex);
  };

  const renderPagination = () => {
    if (!fullFileData.length) return null;
    const totalPreviewPages = Math.ceil((fullFileData.length - 1) / PREVIEW_ROWS_PER_PAGE);
    const items = [];

    items.push(<Pagination.First key="first" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />);
    items.push(<Pagination.Prev key="prev" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />);

    for (let number = 1; number <= totalPreviewPages; number++) {
      if (number === 1 || number === totalPreviewPages ||
          (number >= currentPage - 2 && number <= currentPage + 2)) {
        items.push(
          <Pagination.Item key={number} active={number === currentPage} onClick={() => setCurrentPage(number)}>
            {number}
          </Pagination.Item>
        );
      } else if (number === currentPage - 3 || number === currentPage + 3) {
        items.push(<Pagination.Ellipsis key={`ellipsis-${number}`} />);
      }
    }

    items.push(<Pagination.Next key="next" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPreviewPages} />);
    items.push(<Pagination.Last key="last" onClick={() => setCurrentPage(totalPreviewPages)} disabled={currentPage === totalPreviewPages} />);

    return (
      <div className="em-pagination-wrapper">
        <Pagination>{items}</Pagination>
        <small className="em-pagination-info">
          Page {currentPage} sur {totalPreviewPages} • Total : <strong>{totalRows}</strong> contacts
        </small>
      </div>
    );
  };

  // ============================================
  // TÉLÉCHARGEMENT DES MODÈLES
  // ============================================
  const downloadTemplateCSV = () => {
    const headers = ['msisdn', 'message'];
    const examples = ['24177841332', 'Bonjour, votre commande est prête.'];
    const csvContent = [
      headers.join(','),
      examples.join(','),
      '24177513526,"Bonjour, votre rendez-vous est confirmé pour demain."',
      '24177012345,"Cher client, merci pour votre fidélité."'
    ].join('\n');

    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "modele-sms-en-masse.csv";
    link.click();
    showNotification('Modèle CSV téléchargé', 'success');
  };

  const downloadTemplateExcel = () => {
    const data = [
      ['msisdn', 'message'],
      ['24177841332', 'Bonjour, votre commande est prête.'],
      ['24177513526', 'Bonjour, votre rendez-vous est confirmé.'],
      ['24177012345', 'Cher client, merci pour votre fidélité.'],
    ];
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(workbook, worksheet, "SMS");
    XLSX.writeFile(workbook, "modele-sms-en-masse.xlsx");
    showNotification('Modèle Excel téléchargé', 'success');
  };

  // ============================================
  // SCISSION DU FICHIER
  // ============================================
  const splitFileIntoTwoParts = (data) => {
    const headers = data[0];
    const totalRowsData = data.length - 1;
    const midPoint = Math.floor(totalRowsData / 2);
    const part1 = [headers, ...data.slice(1, midPoint + 1)];
    const part2 = [headers, ...data.slice(midPoint + 1)];
    return { part1, part2 };
  };

  const createFileFromData = async (data, originalFileName, partNumber) => {
    const csvContent = data.map(row =>
      row.map(cell => {
        if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"'))) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(',')
    ).join('\n');

    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = originalFileName.replace(/\.[^/.]+$/, '') + `_part${partNumber}.csv`;
    return new File([blob], fileName, { type: 'text/csv' });
  };

  const uploadPart = async (partData, partNumber) => {
    const partFile = await createFileFromData(partData, selectedFile.name, partNumber);
    const formData = new FormData();
    formData.append('file', partFile);
    formData.append('sender_id', selectedSender);
    formData.append('part', partNumber);
    formData.append('total_parts', 2);

    const response = await fetch(API_CONFIG.proxyUrl, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });

    if (!response.ok) throw new Error(`Erreur partie ${partNumber}: ${response.status}`);
    return await response.json();
  };

  // ============================================
  // GESTION DU FICHIER
  // ============================================
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      showNotification(`Fichier trop volumineux. Max: ${MAX_FILE_SIZE / (1024 * 1024)} Mo`, 'danger');
      return;
    }

    const allowedExtensions = ['.csv', '.xls', '.xlsx'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      showNotification('Format non supporté. Utilisez CSV ou Excel.', 'warning');
      return;
    }

    setSelectedFile(file);
    setUploadLoading(true);
    setUploadProgress(0);
    setUploadStatus('Préparation du fichier...');

    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) { clearInterval(progressInterval); return 90; }
        return prev + 10;
      });
    }, 200);

    try {
      await previewFile(file);
      setUploadStatus('Fichier chargé avec succès !');
      setUploadProgress(100);
      showNotification(`Fichier "${file.name}" chargé`, 'success');
    } catch (error) {
      setUploadStatus('Erreur lors du chargement');
      showNotification(`Erreur: ${error.message}`, 'danger');
    } finally {
      clearInterval(progressInterval);
      setTimeout(() => {
        setUploadLoading(false);
        setUploadProgress(0);
        setUploadStatus('');
      }, 1000);
    }
  };

  const previewFile = (file) => {
    return new Promise((resolve, reject) => {
      setPreviewLoading(true);
      setError(null);
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          let fullData = [];
          const fileName = file.name.toLowerCase();
          const isCSV = fileName.endsWith('.csv');
          const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

          if (isCSV) {
            const content = e.target.result;
            const cleanContent = content.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
            const firstLine = cleanContent.split('\n')[0];
            const separator = firstLine.includes(';') ? ';' : ',';
            const lines = cleanContent.split('\n').filter(line => line.trim() !== '');

            fullData = lines.map(line => {
              const cells = [];
              let currentCell = '';
              let inQuotes = false;
              for (let i = 0; i < line.length; i++) {
                const char = line[i];
                if (char === '"') inQuotes = !inQuotes;
                else if (char === separator && !inQuotes) {
                  cells.push(currentCell.trim());
                  currentCell = '';
                } else currentCell += char;
              }
              cells.push(currentCell.trim());
              return cells.map(cell => cell.replace(/^"|"$/g, '').trim());
            });
          } else if (isExcel) {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
            fullData = jsonData.filter(row => row && row.some(cell => cell && cell.toString().trim() !== ''));
          }

          if (fullData.length === 0) throw new Error('Fichier vide');
          if (fullData[0].length === 0) throw new Error('En-têtes manquants');

          const dataRows = fullData.length - 1;
          setTotalRows(dataRows);

          if (dataRows > MAX_ROWS) {
            showNotification(`Max: ${MAX_ROWS} lignes`, 'warning');
            setPreviewLoading(false);
            reject(new Error('Trop de lignes'));
            return;
          }

          setFullFileData(fullData);
          setFilePreview(fullData.slice(0, 6));

          const headers = fullData[0].map(h => String(h || '').toLowerCase().trim());
          const autoMapping = detectAutoMapping(headers);
          setMapping(autoMapping);

          const hasMessages = fullData.slice(1, 10).some((row) => {
            const messageIndex = autoMapping.message;
            if (messageIndex === undefined || messageIndex >= row.length) return false;
            return row[messageIndex] && row[messageIndex].toString().trim() !== '';
          });

          setMessageSource(hasMessages ? 'file' : 'form');
          setImportStep(2);
          resolve();
        } catch (error) {
          showNotification(`Erreur: ${error.message}`, 'danger');
          reject(error);
        } finally {
          setPreviewLoading(false);
        }
      };

      reader.onerror = () => {
        showNotification('Erreur de lecture', 'danger');
        setPreviewLoading(false);
        reject();
      };

      if (file.name.toLowerCase().endsWith('.csv')) {
        reader.readAsText(file, 'UTF-8');
      } else {
        reader.readAsArrayBuffer(file);
      }
    });
  };

  // ============================================
  // VALIDATION
  // ============================================
  const isValidPhoneNumber = (numero) => {
    const cleaned = numero.replace(/\s+/g, '');
    return /^241[0-9]{8}$/.test(cleaned) || /^\+?[0-9]{10,15}$/.test(cleaned);
  };

  const validateData = () => {
    if (mapping.msisdn === undefined) {
      showNotification('Mappez la colonne des numéros', 'warning');
      return;
    }

    const results = { valid: 0, invalid: 0, duplicates: 0, errors: [], hasFileMessages: false };
    const hasFileMessages = fullFileData.slice(1).some(row => {
      const messageIndex = mapping.message;
      return messageIndex !== undefined && row[messageIndex] && String(row[messageIndex]).trim() !== '';
    });
    results.hasFileMessages = hasFileMessages;

    const seenNumbers = new Set();
    fullFileData.slice(1).forEach((row, index) => {
      const msisdn = row[mapping.msisdn];
      const msisdnStr = msisdn ? String(msisdn).trim() : '';

      if (!msisdnStr) { results.invalid++; results.errors.push(`Ligne ${index + 2}: Numéro manquant`); }
      else if (!isValidPhoneNumber(msisdnStr)) { results.invalid++; results.errors.push(`Ligne ${index + 2}: Invalide "${msisdnStr}"`); }
      else if (seenNumbers.has(msisdnStr)) { results.duplicates++; results.errors.push(`Ligne ${index + 2}: Doublon "${msisdnStr}"`); }
      else { results.valid++; seenNumbers.add(msisdnStr); }
    });

    setValidationResults(results);
    setMessageSource(hasFileMessages ? 'file' : 'form');

    if (results.invalid > 0) {
      showNotification(`${results.invalid} numéros invalides détectés`, 'warning');
    } else {
      showNotification(`${results.valid} numéros valides. Prêt !`, 'success');
    }

    setImportStep(3);
  };

  // ============================================
  // HANDLERS
  // ============================================
  const handleInputChange = (field, value) => {
    setMessageData(prev => ({ ...prev, [field]: value }));
  };

  const handleSenderChange = (senderId) => setSelectedSender(senderId);

  const calculateCost = () => {
    const baseCost = messageData.type === "SMS Pro" ? 4.8 : 3.2;
    const optionsCost = messageData.options.messageLong ? 1.5 : 0;
    return (baseCost + optionsCost) * totalRows;
  };

  const validateForm = () => {
    if (messageSource === 'form') return messageData.texte.trim() !== "" && totalRows > 0;
    return totalRows > 0;
  };

  // ============================================
  // UPLOAD
  // ============================================
  const uploadFileViaProxy = async () => {
    if (!selectedFile) { showNotification('Aucun fichier sélectionné', 'warning'); return; }
    if (!selectedSender) { showNotification('Sélectionnez un sender', 'warning'); return; }
    if (totalRows > MAX_ROWS) { showNotification(`Max ${MAX_ROWS}`, 'danger'); return; }

    const shouldSplit = selectedFile.size > SIZE_THRESHOLD || totalRows > ROWS_THRESHOLD;

    if (shouldSplit) {
      showConfirmDialog(
        'Fichier volumineux détecté',
        `Le fichier est volumineux (${(selectedFile.size / (1024 * 1024)).toFixed(2)} Mo, ${totalRows} lignes).`,
        () => uploadSplitInTwoParts(),
        {
          size: selectedFile.size,
          rows: totalRows,
          part1Rows: Math.floor(totalRows / 2),
          part2Rows: Math.ceil(totalRows / 2)
        }
      );
    } else {
      await uploadSingleFile();
    }
  };

  const uploadSplitInTwoParts = async () => {
    setShowConfirmModal(false);
    setSending(true);
    setImportStatus('importing');
    setImportProgress(0);
    setSplitMode(true);

    try {
      setUploadPhase('splitting');
      setUploadStatus('Scission du fichier en 2 parties...');

      const { part1, part2 } = splitFileIntoTwoParts(fullFileData);
      setPart1Data(part1);
      setPart2Data(part2);

      setCurrentPart(1);
      setUploadPhase('uploading');
      setUploadStatus('Envoi partie 1/2...');
      setImportProgress(20);

      let result1;
      try {
        result1 = await uploadPart(part1, 1);
        setPart1Result(result1);
        showNotification('Partie 1/2 envoyée', 'success');
      } catch (error) {
        setPart1Result({ error: error.message, success: false });
        throw new Error(`Échec partie 1: ${error.message}`);
      }

      setImportProgress(50);
      setCurrentPart(2);
      setUploadStatus('Envoi partie 2/2...');

      let result2;
      try {
        result2 = await uploadPart(part2, 2);
        setPart2Result(result2);
        showNotification('Partie 2/2 envoyée', 'success');
      } catch (error) {
        setPart2Result({ error: error.message, success: false });
        throw new Error(`Échec partie 2: ${error.message}`);
      }

      setImportProgress(90);
      setUploadPhase('finalizing');
      setUploadStatus('Finalisation...');

      const aggregatedResult = {
        success: true,
        parts: 2,
        part1: result1,
        part2: result2,
        totalRows,
        part1Rows: part1.length - 1,
        part2Rows: part2.length - 1,
        message: 'Fichier scindé en 2 parties et envoyé'
      };

      setImportProgress(100);
      setUploadResults(aggregatedResult);
      setImportStatus('success');
      setImportStats({
        total: totalRows,
        imported: validationResults.valid,
        failed: validationResults.invalid,
        parts: 2
      });

      showNotification('Fichier envoyé en 2 parties !', 'success');
      setImportStep(3);

    } catch (error) {
      setImportStatus('error');
      setUploadResults({ error: error.message, success: false, part1: part1Result, part2: part2Result });
      showNotification(`Erreur: ${error.message}`, 'danger');
      setImportStep(3);
    } finally {
      setSending(false);
      setUploadPhase('');
    }
  };

  const uploadSingleFile = async () => {
    setSending(true);
    setImportStatus('importing');
    setImportProgress(0);
    setSplitMode(false);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
      showNotification('Timeout dépassé', 'warning');
    }, UPLOAD_TIMEOUT);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('sender_id', selectedSender);

      const url = `${API_CONFIG.proxyUrl}?_=${Date.now()}`;
      setImportProgress(30);

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      setImportProgress(70);

      if (!response.ok) {
        let errorText;
        try { errorText = await response.text(); } catch { errorText = 'Erreur'; }
        throw new Error(`Erreur ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      setImportProgress(100);
      setUploadResults(result);
      setImportStatus('success');
      setImportStats({
        total: totalRows,
        imported: validationResults.valid,
        failed: validationResults.invalid,
        jobId: result.job_id || result.id || null
      });

      showNotification('Fichier envoyé !', 'success');
      setImportStep(3);

    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        setImportStatus('error');
        setUploadResults({ error: 'Timeout. Passage en scission...', timeout: true });
        showNotification('Timeout. Passage en scission...', 'warning');
        showConfirmDialog(
          'Timeout détecté',
          'Le fichier est trop volumineux pour un envoi direct. Scinder en 2 parties ?',
          () => uploadSplitInTwoParts()
        );
      } else {
        setImportStatus('error');
        setUploadResults({ error: error.message, success: false });
        showNotification(`Erreur: ${error.message}`, 'danger');
        setImportStep(3);
      }
    } finally {
      setSending(false);
    }
  };

  const resetImport = () => {
    setImportStep(1);
    setSelectedFile(null);
    setFilePreview([]);
    setFullFileData([]);
    setMapping({});
    setValidationResults({});
    setImportProgress(0);
    setImportStatus('idle');
    setImportStats({});
    setUploadResults(null);
    setTotalRows(0);
    setCurrentPage(1);
    setSplitMode(false);
    setPart1Data([]);
    setPart2Data([]);
    setCurrentPart(1);
    setPart1Result(null);
    setPart2Result(null);
    setMessageData({
      texte: "", destinataires: "", type: "SMS Pro", reference: "",
      options: { accuséReception: false, messageLong: false, personnalisation: false }
    });
    setMessageSource('form');
    showNotification('Formulaire réinitialisé', 'info');
  };

  const shouldSplit = selectedFile && (selectedFile.size > SIZE_THRESHOLD || totalRows > ROWS_THRESHOLD);

  return (
    <div className="em-page main main-app p-3 p-lg-4">
      {/* ========== TOAST ========== */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast show={showToast} onClose={() => setShowToast(false)} bg={toastVariant} delay={5000} autohide>
          <Toast.Header closeButton>
            <strong className="me-auto">
              {toastVariant === 'success' && '✅ Succès'}
              {toastVariant === 'danger' && '❌ Erreur'}
              {toastVariant === 'warning' && '⚠️ Attention'}
              {toastVariant === 'info' && 'ℹ️ Information'}
            </strong>
          </Toast.Header>
          <Toast.Body className={toastVariant === 'danger' || toastVariant === 'success' ? 'text-white' : ''}>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* ========== MODAL CONFIRMATION ========== */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered className="em-modal">
        <div className="em-modal-header em-modal-header-warning">
          <div className="d-flex align-items-center gap-3">
            <div className="em-modal-icon em-modal-icon-warning">
              <i className="ri-alert-line"></i>
            </div>
            <div>
              <h5 className="em-modal-title mb-0">{confirmModalConfig.title}</h5>
              <small className="em-modal-subtitle">Action requise</small>
            </div>
          </div>
          <button className="em-modal-close" onClick={() => setShowConfirmModal(false)}>
            <i className="ri-close-line"></i>
          </button>
        </div>
        <Modal.Body className="em-modal-body">
          <p className="text-secondary mb-3">{confirmModalConfig.message}</p>
          {confirmModalConfig.data && (
            <div className="em-modal-data">
              <div className="em-modal-data-row">
                <span className="em-modal-data-label">Taille fichier</span>
                <span className="em-modal-data-value">
                  {(confirmModalConfig.data.size / (1024 * 1024)).toFixed(2)} Mo
                </span>
              </div>
              <div className="em-modal-data-row">
                <span className="em-modal-data-label">Lignes totales</span>
                <span className="em-modal-data-value">{confirmModalConfig.data.rows}</span>
              </div>
              <div className="em-modal-data-row">
                <span className="em-modal-data-label">Partie 1</span>
                <span className="em-modal-data-value em-modal-data-value-blue">
                  {confirmModalConfig.data.part1Rows} lignes
                </span>
              </div>
              <div className="em-modal-data-row">
                <span className="em-modal-data-label">Partie 2</span>
                <span className="em-modal-data-value em-modal-data-value-blue">
                  {confirmModalConfig.data.part2Rows} lignes
                </span>
              </div>
            </div>
          )}
          <p className="text-secondary small mb-0 mt-3">
            Le fichier sera automatiquement scindé en 2 parties pour faciliter l'envoi.
          </p>
        </Modal.Body>
        <div className="em-modal-footer">
          <button className="em-btn-cancel" onClick={() => setShowConfirmModal(false)}>
            Annuler
          </button>
          <button className="em-btn-primary" onClick={() => confirmModalConfig.onConfirm?.()}>
            Continuer
          </button>
        </div>
      </Modal>

      {/* ========== HEADER ========== */}
      <div className="em-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="em-header-icon">
              <i className="ri-file-upload-line"></i>
            </div>
            <div>
              <h3 className="em-header-title mb-1">Envoi de messages en masse</h3>
              <p className="em-header-subtitle mb-0">
                Importez votre fichier de contacts pour un envoi groupé
              </p>
              {shouldSplit && (
                <span className="em-header-badge mt-2">
                  <i className="ri-alert-line"></i>
                  Fichier volumineux — Sera scindé en 2 parties
                </span>
              )}
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button as={Link} to="/apps/EnvoieMasseHome" className="em-btn-secondary">
              <i className="ri-arrow-left-line"></i>
              <span>Retour</span>
            </Button>
            <Button onClick={downloadTemplateCSV} className="em-btn-secondary">
              <i className="ri-file-text-line"></i>
              <span>Modèle CSV</span>
            </Button>
            <Button onClick={downloadTemplateExcel} className="em-btn-secondary">
              <i className="ri-file-excel-line"></i>
              <span>Modèle Excel</span>
            </Button>
          </div>
        </div>
      </div>

      {/* ========== STEPPER ========== */}
      <div className="em-stepper mb-4">
        <div className="em-stepper-wrapper">
          {[
            { n: 1, label: 'Import', icon: 'ri-upload-cloud-line' },
            { n: 2, label: 'Validation', icon: 'ri-check-double-line' },
            { n: 3, label: 'Résultat', icon: 'ri-trophy-line' }
          ].map((step, i) => (
            <React.Fragment key={step.n}>
              <div className={`em-step ${importStep >= step.n ? 'active' : ''} ${importStep === step.n ? 'current' : ''}`}>
                <div className="em-step-circle">
                  {importStep > step.n ? (
                    <i className="ri-check-line"></i>
                  ) : (
                    <i className={step.icon}></i>
                  )}
                </div>
                <div className="em-step-label">{step.label}</div>
              </div>
              {i < 2 && (
                <div className={`em-step-line ${importStep > step.n ? 'active' : ''}`}></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ========== ÉTAPE 1 : UPLOAD ========== */}
      {importStep === 1 && (
        <Row>
          <Col lg={8} className="mx-auto">
            <div className="em-upload-card">
              <div className="em-upload-header">
                <div className="d-flex align-items-center gap-2">
                  <div className="em-upload-icon">
                    <i className="ri-cloud-line"></i>
                  </div>
                  <div>
                    <h6 className="em-upload-title mb-0">Importer le fichier</h6>
                    <small className="em-upload-subtitle">Glissez-déposez ou cliquez pour sélectionner</small>
                  </div>
                </div>
              </div>

              <div className="em-upload-body">
                <div
                  className="em-dropzone"
                  onClick={() => document.getElementById('em-file-input')?.click()}
                >
                  <div className="em-dropzone-icon">
                    <i className="ri-file-upload-line"></i>
                  </div>
                  <h5 className="em-dropzone-title">Glissez-déposez votre fichier ici</h5>
                  <p className="em-dropzone-text">
                    Formats acceptés : <strong>CSV, Excel (.xlsx, .xls)</strong><br />
                    Taille maximum : <strong>{MAX_FILE_SIZE / (1024 * 1024)} Mo</strong>
                  </p>

                  {uploadLoading ? (
                    <div className="em-upload-progress">
                      <div className="em-progress">
                        <div className="em-progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                      </div>
                      <p className="em-upload-status">
                        <i className="ri-loader-4-line spinning"></i>
                        {uploadStatus}
                      </p>
                    </div>
                  ) : (
                    <button className="em-btn-primary" type="button">
                      <i className="ri-folder-open-line"></i>
                      Sélectionner un fichier
                    </button>
                  )}

                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    id="em-file-input"
                  />
                </div>

                <div className="em-info-alert mt-4">
                  <i className="ri-information-line"></i>
                  <div>
                    <strong>Maximum {MAX_ROWS.toLocaleString('fr-FR')} messages</strong> par envoi
                  </div>
                </div>

                <div className="em-format-section mt-4">
                  <h6 className="em-format-title">
                    <i className="ri-table-line"></i>
                    Format attendu
                  </h6>
                  <div className="em-format-table">
                    <div className="em-format-row em-format-header">
                      <div className="em-format-cell">Colonne</div>
                      <div className="em-format-cell">Obligatoire</div>
                      <div className="em-format-cell">Exemple</div>
                    </div>
                    <div className="em-format-row">
                      <div className="em-format-cell">
                        <code className="em-code">msisdn</code>
                        <span className="em-required">*</span>
                      </div>
                      <div className="em-format-cell">
                        <span className="em-badge em-badge-danger">Oui</span>
                      </div>
                      <div className="em-format-cell">24177841332</div>
                    </div>
                    <div className="em-format-row">
                      <div className="em-format-cell">
                        <code className="em-code">message</code>
                      </div>
                      <div className="em-format-cell">
                        <span className="em-badge em-badge-gray">Optionnel</span>
                      </div>
                      <div className="em-format-cell">Bonjour...</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* ========== ÉTAPE 2 : VALIDATION ========== */}
      {importStep === 2 && (
        <Row>
          <Col lg={8}>
            <div className="em-validation-card mb-4">
              <div className="em-validation-header">
                <div className="d-flex align-items-center gap-2">
                  <div className="em-validation-icon">
                    <i className="ri-check-double-line"></i>
                  </div>
                  <div>
                    <h6 className="em-validation-title mb-0">Validation des données</h6>
                    <small className="em-validation-subtitle">Vérifiez et configurez votre envoi</small>
                  </div>
                </div>
                {messageSource === 'file' && (
                  <span className="em-badge em-badge-success">
                    <i className="ri-file-text-line"></i>
                    Messages personnalisés
                  </span>
                )}
              </div>

              <div className="em-validation-body">
                {shouldSplit && (
                  <div className="em-alert em-alert-warning mb-4">
                    <i className="ri-alert-line"></i>
                    <div>
                      <strong>Fichier volumineux</strong> — {totalRows} messages ({(selectedFile.size / (1024 * 1024)).toFixed(2)} Mo).
                      L'envoi sera automatiquement scindé en <strong>2 parties</strong>.
                    </div>
                  </div>
                )}

                {sending && (
                  <div className="em-sending-state">
                    <div className="em-sending-icon">
                      <i className="ri-send-plane-2-line"></i>
                    </div>
                    <h4 className="em-sending-title">
                      {uploadPhase === 'splitting' && 'Scission du fichier...'}
                      {uploadPhase === 'uploading' && `Envoi partie ${currentPart}/2...`}
                      {uploadPhase === 'finalizing' && 'Finalisation...'}
                      {!uploadPhase && 'Envoi en cours...'}
                    </h4>
                    <div className="em-progress em-progress-large">
                      <div className="em-progress-bar em-progress-bar-animated" style={{ width: `${importProgress}%` }}></div>
                    </div>
                    <p className="em-sending-status">{importProgress}%</p>
                    <p className="em-sending-hint">
                      <i className="ri-time-line"></i>
                      {uploadPhase === 'splitting' && "Préparation des 2 parties..."}
                      {uploadPhase === 'uploading' && `Partie ${currentPart}/2 — ${currentPart === 1 ? 'Première moitié' : 'Deuxième moitié'}`}
                      {uploadPhase === 'finalizing' && "Assemblage des résultats..."}
                    </p>
                  </div>
                )}

                {/* Aperçu */}
                <div className="em-preview-section">
                  <h6 className="em-section-title">
                    <i className="ri-eye-line"></i>
                    Aperçu ({totalRows} contacts)
                  </h6>

                  {previewLoading ? (
                    <div className="em-loading-state">
                      <div className="em-loading-spinner"></div>
                      <p>Chargement de l'aperçu...</p>
                    </div>
                  ) : (
                    <>
                      <div className="em-table-wrapper">
                        <table className="em-table">
                          <thead>
                            <tr>
                              {fullFileData[0]?.map((header, index) => (
                                <th key={index}>
                                  <div className="em-table-th-content">
                                    <span className="em-table-th-index">Col {index + 1}</span>
                                    <span className="em-table-th-label">{header}</span>
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {getCurrentPageData().slice(1).map((row, rowIndex) => (
                              <tr key={rowIndex}>
                                {row.map((cell, cellIndex) => (
                                  <td key={cellIndex}>
                                    {cellIndex === mapping.message && cell ? (
                                      <span className="em-cell-message">{cell}</span>
                                    ) : cellIndex === mapping.msisdn && cell ? (
                                      <span className="em-cell-msisdn">{cell}</span>
                                    ) : (
                                      cell || <span className="text-muted">—</span>
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {renderPagination()}
                    </>
                  )}
                </div>

                {/* Configuration */}
                <div className="em-config-section">
                  <h6 className="em-section-title">
                    <i className="ri-settings-3-line"></i>
                    Configuration de l'envoi
                  </h6>

                  <div className="em-form-group">
                    <label className="em-form-label">Sender ID</label>
                    {loading ? (
                      <div className="em-loading-inline">
                        <Spinner animation="border" size="sm" />
                        <span>Chargement des senders...</span>
                      </div>
                    ) : error ? (
                      <div className="em-alert em-alert-warning">
                        <i className="ri-error-warning-line"></i>
                        {error}
                      </div>
                    ) : (
                      <select
                        className="em-select"
                        value={selectedSender}
                        onChange={(e) => handleSenderChange(e.target.value)}
                      >
                        <option value="">Sélectionner un Sender ID</option>
                        {senderList.map(s => (
                          <option key={s.value} value={s.value}>
                            {s.label} ({s.status})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {messageSource === 'form' && (
                    <div className="em-form-group">
                      <label className="em-form-label">Message</label>
                      <textarea
                        className="em-textarea"
                        rows={5}
                        value={messageData.texte}
                        onChange={(e) => handleInputChange('texte', e.target.value)}
                        placeholder="Rédigez votre message..."
                      />
                      <div className="em-form-hint">
                        <i className="ri-information-line"></i>
                        Ce message sera utilisé pour tous les destinataires
                      </div>
                    </div>
                  )}
                </div>

                <div className="em-actions-row">
                  <button className="em-btn-secondary" onClick={() => setImportStep(1)}>
                    <i className="ri-arrow-left-line"></i>
                    Retour
                  </button>
                  <button className="em-btn-primary" onClick={validateData}>
                    Valider et continuer
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </Col>

          {/* Sidebar résumé */}
          <Col lg={4}>
            <div ref={summaryCardRef} className="em-sidebar sticky-top" style={{ top: '20px', zIndex: 10 }}>
              <div className="em-summary-card">
                <div className="em-summary-header">
                  <div className="em-summary-icon">
                    <i className="ri-file-list-3-line"></i>
                  </div>
                  <div>
                    <h6 className="em-summary-title mb-0">Résumé</h6>
                    <small className="em-summary-subtitle">Vérifiez avant l'envoi</small>
                  </div>
                </div>

                <div className="em-summary-body">
                  <div className="em-summary-item">
                    <span className="em-summary-label">
                      <i className="ri-file-line"></i>
                      Fichier
                    </span>
                    <span className="em-summary-value em-summary-value-truncate">
                      {selectedFile?.name || 'Aucun'}
                    </span>
                  </div>

                  {selectedFile && (
                    <div className="em-summary-item">
                      <span className="em-summary-label">
                        <i className="ri-hard-drive-2-line"></i>
                        Taille
                      </span>
                      <span className="em-summary-value">
                        {(selectedFile.size / (1024 * 1024)).toFixed(2)} Mo
                      </span>
                    </div>
                  )}

                  <div className="em-summary-item">
                    <span className="em-summary-label">
                      <i className="ri-contacts-line"></i>
                      Contacts
                    </span>
                    <span className="em-summary-value em-summary-value-highlight">
                      {totalRows.toLocaleString('fr-FR')}
                      {shouldSplit && (
                        <span className="em-summary-badge">2 parties</span>
                      )}
                    </span>
                  </div>

                  <div className="em-summary-item">
                    <span className="em-summary-label">
                      <i className="ri-file-text-line"></i>
                      Source
                    </span>
                    <span className={`em-summary-badge ${messageSource === 'file' ? 'em-summary-badge-success' : 'em-summary-badge-blue'}`}>
                      {messageSource === 'file' ? 'Fichier' : 'Formulaire'}
                    </span>
                  </div>

                  <div className="em-summary-item">
                    <span className="em-summary-label">
                      <i className="ri-at-line"></i>
                      Sender
                    </span>
                    <span className="em-summary-badge em-summary-badge-info">
                      {selectedSender || '—'}
                    </span>
                  </div>

                  <div className="em-summary-item">
                    <span className="em-summary-label">
                      <i className="ri-check-double-line"></i>
                      Validation
                    </span>
                    <div className="em-summary-badges">
                      <span className="em-summary-badge em-summary-badge-success">
                        {validationResults.valid || 0} valides
                      </span>
                      {validationResults.invalid > 0 && (
                        <span className="em-summary-badge em-summary-badge-danger">
                          {validationResults.invalid} invalides
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="em-summary-divider"></div>

                  <div className="em-summary-cost">
                    <span className="em-summary-cost-label">Coût estimé</span>
                    <span className="em-summary-cost-value">
                      {calculateCost().toFixed(2)} <small>FCFA</small>
                    </span>
                  </div>
                </div>

                <div className="em-summary-footer">
                  <button
                    className="em-btn-primary em-btn-full"
                    onClick={uploadFileViaProxy}
                    disabled={!validateForm() || sending || !selectedSender || totalRows === 0}
                  >
                    {sending ? (
                      <>
                        <i className="ri-loader-4-line spinning"></i>
                        {uploadPhase === 'splitting' ? 'Scission...' :
                         uploadPhase === 'uploading' ? `Partie ${currentPart}/2` :
                         'Envoi...'}
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill"></i>
                        {shouldSplit ? 'Scinder et envoyer (2 parties)' : 'Envoyer'}
                      </>
                    )}
                  </button>

                  {shouldSplit && (
                    <div className="em-summary-hint">
                      <i className="ri-information-line"></i>
                      Scindé en 2 parties : ~{Math.floor(totalRows/2)} et ~{Math.ceil(totalRows/2)} lignes
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* ========== ÉTAPE 3 : RÉSULTAT ========== */}
      {importStep === 3 && (
        <Row>
          <Col lg={8} className="mx-auto">
            <div className={`em-result-card ${importStatus === 'success' ? 'em-result-success' : 'em-result-error'}`}>
              <div className="em-result-header">
                <div className={`em-result-icon ${importStatus === 'success' ? 'em-result-icon-success' : 'em-result-icon-error'}`}>
                  <i className={importStatus === 'success' ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'}></i>
                </div>
                <div>
                  <h4 className="em-result-title">
                    {importStatus === 'success' ? 'Envoi terminé avec succès' : 'Erreur lors de l\'envoi'}
                  </h4>
                  <p className="em-result-subtitle">
                    {splitMode ? 'Fichier scindé en 2 parties' : 'Traitement complet'}
                  </p>
                </div>
              </div>

              <div className="em-result-body">
                {importStatus === 'success' ? (
                  <>
                    <div className="em-result-stats">
                      <div className="em-result-stat em-result-stat-success">
                        <div className="em-result-stat-value">{validationResults.valid || 0}</div>
                        <div className="em-result-stat-label">Valides</div>
                      </div>
                      <div className="em-result-stat em-result-stat-danger">
                        <div className="em-result-stat-value">{validationResults.invalid || 0}</div>
                        <div className="em-result-stat-label">Invalides</div>
                      </div>
                      <div className="em-result-stat em-result-stat-info">
                        <div className="em-result-stat-value">{totalRows}</div>
                        <div className="em-result-stat-label">Total</div>
                      </div>
                    </div>

                    {splitMode && (
                      <div className="em-result-split">
                        <div className="em-result-split-title">
                          <i className="ri-files-line"></i>
                          Détails de la scission
                        </div>
                        <div className="em-result-split-item">
                          <span>Partie 1</span>
                          <span className="em-result-split-value">{Math.floor(totalRows/2)} lignes</span>
                          <i className="ri-checkbox-circle-fill em-result-split-check"></i>
                        </div>
                        <div className="em-result-split-item">
                          <span>Partie 2</span>
                          <span className="em-result-split-value">{Math.ceil(totalRows/2)} lignes</span>
                          <i className="ri-checkbox-circle-fill em-result-split-check"></i>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="em-result-error-message">
                    <i className="ri-error-warning-line"></i>
                    <div>
                      <strong>{uploadResults?.error || 'Erreur inconnue'}</strong>
                      {uploadResults?.timeout && (
                        <p className="small mb-0 mt-2">
                          Le fichier est trop volumineux pour un envoi direct.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="em-result-footer">
                {importStatus === 'success' ? (
                  <>
                    <button className="em-btn-secondary" onClick={resetImport}>
                      <i className="ri-add-line"></i>
                      Nouvel envoi
                    </button>
                    <Link to="/apps/EnvoieMasseHome" className="em-btn-primary">
                      <i className="ri-history-line"></i>
                      Historique
                    </Link>
                  </>
                ) : (
                  <>
                    <button className="em-btn-secondary" onClick={() => setImportStep(2)}>
                      <i className="ri-arrow-left-line"></i>
                      Retour
                    </button>
                    <button className="em-btn-primary" onClick={resetImport}>
                      <i className="ri-refresh-line"></i>
                      Réessayer
                    </button>
                  </>
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .em-page {
          background: #f8fafc;
          min-height: 100vh;
        }
        .em-page * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ========== HEADER ========== */
        .em-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .em-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
        }
        .em-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .em-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }
        .em-header-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 20px;
          background: #fef3c7;
          color: #d97706;
          font-size: 0.72rem;
          font-weight: 700;
        }

        /* ========== BUTTONS ========== */
        .em-btn-primary, .em-btn-secondary, .em-btn-cancel {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 11px 20px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s;
          cursor: pointer;
          text-decoration: none;
        }
        .em-btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }
        .em-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .em-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .em-btn-secondary {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .em-btn-secondary:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #0f172a;
        }
        .em-btn-cancel {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .em-btn-cancel:hover {
          background: #f1f5f9;
        }
        .em-btn-full {
          width: 100%;
        }

        /* ========== STEPPER ========== */
        .em-stepper {
          background: white;
          border-radius: 16px;
          padding: 24px 32px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .em-stepper-wrapper {
          display: flex;
          align-items: center;
          justify-content: space-between;
          max-width: 700px;
          margin: 0 auto;
        }
        .em-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }
        .em-step-circle {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: #f1f5f9;
          color: #94a3b8;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          transition: all 0.3s;
          border: 2px solid #e2e8f0;
        }
        .em-step.active .em-step-circle {
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          color: white;
          border-color: transparent;
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .em-step.current .em-step-circle {
          transform: scale(1.05);
        }
        .em-step-label {
          font-size: 0.75rem;
          font-weight: 700;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .em-step.active .em-step-label {
          color: #0f172a;
        }
        .em-step-line {
          flex-grow: 1;
          height: 2px;
          background: #e2e8f0;
          margin: 0 12px;
          margin-bottom: 28px;
          transition: all 0.3s;
        }
        .em-step-line.active {
          background: linear-gradient(90deg, #3b82f6, #8b5cf6);
        }

        /* ========== UPLOAD CARD ========== */
        .em-upload-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .em-upload-header {
          padding: 20px 24px;
          background: linear-gradient(180deg, #ffffff, #fafbfc);
          border-bottom: 1px solid #f1f5f9;
        }
        .em-upload-icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          background: #eff6ff;
          color: #3b82f6;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .em-upload-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.95rem;
          margin: 0;
        }
        .em-upload-subtitle {
          color: #94a3b8;
          font-size: 0.75rem;
        }
        .em-upload-body {
          padding: 32px;
        }
        .em-dropzone {
          border: 2px dashed #cbd5e1;
          border-radius: 16px;
          padding: 48px 24px;
          text-align: center;
          cursor: pointer;
          background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
          transition: all 0.3s;
        }
        .em-dropzone:hover {
          border-color: #3b82f6;
          background: linear-gradient(135deg, #eff6ff 0%, #ffffff 100%);
          transform: scale(1.005);
        }
        .em-dropzone-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          margin-bottom: 20px;
          box-shadow: 0 12px 24px -8px rgba(59, 130, 246, 0.5);
        }
        .em-dropzone-title {
          color: #0f172a;
          font-weight: 700;
          margin-bottom: 12px;
        }
        .em-dropzone-text {
          color: #64748b;
          font-size: 0.875rem;
          margin-bottom: 24px;
          line-height: 1.7;
        }
        .em-upload-progress {
          max-width: 400px;
          margin: 20px auto 0;
        }
        .em-progress {
          height: 8px;
          background: #f1f5f9;
          border-radius: 10px;
          overflow: hidden;
          margin-bottom: 12px;
        }
        .em-progress-large {
          height: 12px;
          max-width: 500px;
          margin: 20px auto;
        }
        .em-progress-bar {
          height: 100%;
          background: linear-gradient(90deg, #3b82f6, #8b5cf6);
          border-radius: 10px;
          transition: width 0.3s ease;
        }
        .em-progress-bar-animated {
          background: linear-gradient(90deg, #3b82f6, #8b5cf6, #3b82f6);
          background-size: 200% 100%;
          animation: em-progress-anim 2s linear infinite;
        }
        @keyframes em-progress-anim {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        .em-upload-status {
          color: #3b82f6;
          font-weight: 600;
          font-size: 0.875rem;
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        /* Info alert */
        .em-info-alert {
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          color: #1e40af;
          padding: 14px 18px;
          border-radius: 12px;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          gap: 10px;
          border-left: 4px solid #3b82f6;
        }
        .em-info-alert i {
          font-size: 1.3rem;
          flex-shrink: 0;
        }

        /* Format table */
        .em-format-section {
          background: #f8fafc;
          border-radius: 12px;
          padding: 20px;
        }
        .em-format-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.9rem;
          margin-bottom: 16px;
        }
        .em-format-title i {
          color: #3b82f6;
        }
        .em-format-table {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .em-format-row {
          display: grid;
          grid-template-columns: 1fr 1fr 1.5fr;
          gap: 12px;
          padding: 12px 16px;
          background: white;
          border-radius: 8px;
          align-items: center;
          border: 1px solid #e2e8f0;
        }
        .em-format-header {
          background: transparent;
          border: none;
          padding: 8px 16px;
          font-weight: 700;
          color: #64748b;
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .em-format-cell {
          font-size: 0.85rem;
          color: #334155;
        }
        .em-code {
          font-family: 'SF Mono', Menlo, Monaco, monospace;
          background: #f1f5f9;
          color: #0f172a;
          padding: 3px 8px;
          border-radius: 5px;
          font-size: 0.8rem;
          font-weight: 600;
        }
        .em-required {
          color: #ef4444;
          margin-left: 4px;
          font-weight: 700;
        }
        .em-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 10px;
          border-radius: 6px;
          font-size: 0.72rem;
          font-weight: 700;
        }
        .em-badge-danger { background: #fee2e2; color: #ef4444; }
        .em-badge-gray { background: #f1f5f9; color: #64748b; }
        .em-badge-success { background: #d1fae5; color: #059669; }

        /* ========== VALIDATION CARD ========== */
        .em-validation-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .em-validation-header {
          padding: 20px 24px;
          background: linear-gradient(180deg, #ffffff, #fafbfc);
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .em-validation-icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          background: #d1fae5;
          color: #10b981;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .em-validation-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.95rem;
          margin: 0;
        }
        .em-validation-subtitle {
          color: #94a3b8;
          font-size: 0.75rem;
        }
        .em-validation-body {
          padding: 24px;
        }

        /* Alerts */
        .em-alert {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 14px 18px;
          border-radius: 12px;
          font-size: 0.85rem;
        }
        .em-alert-warning {
          background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
          color: #92400e;
          border-left: 4px solid #f59e0b;
        }
        .em-alert-warning i {
          color: #f59e0b;
          font-size: 1.3rem;
          flex-shrink: 0;
        }

        /* Sending state */
        .em-sending-state {
          text-align: center;
          padding: 40px 20px;
          background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%);
          border-radius: 16px;
          margin-bottom: 24px;
          border: 1px solid #bfdbfe;
        }
        .em-sending-icon {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          margin-bottom: 20px;
          animation: em-pulse 2s ease-in-out infinite;
          box-shadow: 0 12px 24px -8px rgba(59, 130, 246, 0.5);
        }
        @keyframes em-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .em-sending-title {
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 16px;
        }
        .em-sending-status {
          color: #3b82f6;
          font-weight: 700;
          font-size: 1.1rem;
          margin: 0 0 8px;
        }
        .em-sending-hint {
          color: #64748b;
          font-size: 0.8rem;
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        /* Section title */
        .em-section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.9rem;
          margin-bottom: 16px;
        }
        .em-section-title i {
          color: #3b82f6;
          font-size: 1.1rem;
        }

        /* Table */
        .em-preview-section {
          margin-bottom: 24px;
        }
        .em-table-wrapper {
          overflow-x: auto;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          margin-bottom: 16px;
        }
        .em-table {
          width: 100%;
          font-size: 0.82rem;
        }
        .em-table thead th {
          background: #f8fafc;
          color: #475569;
          font-weight: 700;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 12px 16px;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
        }
        .em-table-th-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .em-table-th-index {
          color: #3b82f6;
          font-size: 0.65rem;
        }
        .em-table-th-label {
          color: #0f172a;
        }
        .em-table tbody td {
          padding: 10px 16px;
          border-bottom: 1px solid #f1f5f9;
          color: #334155;
        }
        .em-table tbody tr:hover {
          background: #f8fafc;
        }
        .em-cell-message {
          color: #3b82f6;
          font-weight: 600;
        }
        .em-cell-msisdn {
          color: #10b981;
          font-weight: 600;
          font-family: 'SF Mono', Menlo, monospace;
          font-size: 0.78rem;
        }
        .em-pagination-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .em-pagination-info {
          color: #64748b;
          font-size: 0.78rem;
        }

        /* Loading */
        .em-loading-state {
          text-align: center;
          padding: 60px 20px;
          color: #64748b;
        }
        .em-loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: em-spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        @keyframes em-spin {
          to { transform: rotate(360deg); }
        }
        .em-loading-inline {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          color: #64748b;
          font-size: 0.85rem;
        }

        /* Config section */
        .em-config-section {
          margin-bottom: 24px;
        }
        .em-form-group {
          margin-bottom: 18px;
        }
        .em-form-label {
          display: block;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.85rem;
          margin-bottom: 8px;
        }
        .em-select, .em-textarea {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          color: #0f172a;
          background: white;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
        }
        .em-select:focus, .em-textarea:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }
        .em-textarea {
          resize: vertical;
          line-height: 1.5;
        }
        .em-form-hint {
          margin-top: 8px;
          font-size: 0.75rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .em-form-hint i {
          color: #3b82f6;
        }

        /* Actions */
        .em-actions-row {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding-top: 20px;
          border-top: 1px solid #f1f5f9;
        }

        /* ========== SIDEBAR ========== */
        .em-sidebar {
          position: sticky;
        }
        .em-summary-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 4px 12px -4px rgba(0,0,0,0.05);
        }
        .em-summary-header {
          padding: 20px 24px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: white;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .em-summary-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: rgba(59, 130, 246, 0.2);
          color: #60a5fa;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }
        .em-summary-title {
          font-weight: 700;
          color: white;
          margin: 0;
          font-size: 0.95rem;
        }
        .em-summary-subtitle {
          color: #94a3b8;
          font-size: 0.72rem;
        }
        .em-summary-body {
          padding: 20px 24px;
        }
        .em-summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #f1f5f9;
        }
        .em-summary-item:last-child {
          border-bottom: none;
        }
        .em-summary-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 0.8rem;
          color: #64748b;
          font-weight: 500;
        }
        .em-summary-label i {
          color: #94a3b8;
          font-size: 0.95rem;
        }
        .em-summary-value {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.85rem;
          text-align: right;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .em-summary-value-truncate {
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .em-summary-value-highlight {
          color: #3b82f6;
          font-size: 1.1rem;
        }
        .em-summary-badges {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }
        .em-summary-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .em-summary-badge-blue { background: #eff6ff; color: #3b82f6; }
        .em-summary-badge-success { background: #d1fae5; color: #059669; }
        .em-summary-badge-info { background: #cffafe; color: #0891b2; }
        .em-summary-badge-danger { background: #fee2e2; color: #ef4444; }
        .em-summary-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
          margin: 16px 0;
        }
        .em-summary-cost {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
        }
        .em-summary-cost-label {
          font-size: 0.8rem;
          color: #64748b;
          font-weight: 600;
        }
        .em-summary-cost-value {
          font-size: 1.4rem;
          font-weight: 700;
          color: #10b981;
        }
        .em-summary-cost-value small {
          font-size: 0.75rem;
          color: #94a3b8;
          font-weight: 600;
        }
        .em-summary-footer {
          padding: 20px 24px;
          border-top: 1px solid #f1f5f9;
          background: #fafbfc;
        }
        .em-summary-hint {
          text-align: center;
          font-size: 0.72rem;
          color: #d97706;
          margin-top: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          background: #fef3c7;
          padding: 8px 12px;
          border-radius: 8px;
        }

        /* ========== MODAL ========== */
        .em-modal .modal-content {
          border: none;
          border-radius: 16px;
          overflow: hidden;
        }
        .em-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          color: white;
        }
        .em-modal-header-warning {
          background: linear-gradient(135deg, #d97706 0%, #f59e0b 100%);
        }
        .em-modal-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: rgba(255,255,255,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .em-modal-title {
          font-weight: 700;
          margin: 0;
          letter-spacing: -0.01em;
        }
        .em-modal-subtitle {
          color: rgba(255,255,255,0.8);
          font-size: 0.8rem;
        }
        .em-modal-close {
          background: rgba(255,255,255,0.1);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 1.1rem;
        }
        .em-modal-close:hover {
          background: rgba(255,255,255,0.2);
          transform: rotate(90deg);
        }
        .em-modal-body {
          padding: 24px;
          background: #f8fafc;
        }
        .em-modal-data {
          background: white;
          border-radius: 12px;
          padding: 16px 20px;
          border: 1px solid #e2e8f0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .em-modal-data-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .em-modal-data-label {
          font-size: 0.82rem;
          color: #64748b;
          font-weight: 500;
        }
        .em-modal-data-value {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.875rem;
        }
        .em-modal-data-value-blue {
          color: #3b82f6;
        }
        .em-modal-footer {
          padding: 16px 24px;
          background: white;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }

        /* ========== RESULT CARD ========== */
        .em-result-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 4px 12px -4px rgba(0,0,0,0.05);
        }
        .em-result-success {
          border-top: 4px solid #10b981;
        }
        .em-result-error {
          border-top: 4px solid #ef4444;
        }
        .em-result-header {
          padding: 32px;
          text-align: center;
          background: linear-gradient(180deg, #ffffff, #fafbfc);
          border-bottom: 1px solid #f1f5f9;
        }
        .em-result-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 40px;
          margin-bottom: 16px;
        }
        .em-result-icon-success {
          background: linear-gradient(135deg, #d1fae5, #a7f3d0);
          color: #10b981;
          box-shadow: 0 12px 24px -8px rgba(16, 185, 129, 0.4);
        }
        .em-result-icon-error {
          background: linear-gradient(135deg, #fee2e2, #fecaca);
          color: #ef4444;
          box-shadow: 0 12px 24px -8px rgba(239, 68, 68, 0.4);
        }
        .em-result-title {
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 6px;
          font-size: 1.4rem;
        }
        .em-result-subtitle {
          color: #64748b;
          font-size: 0.875rem;
          margin: 0;
        }
        .em-result-body {
          padding: 32px;
        }
        .em-result-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }
        .em-result-stat {
          text-align: center;
          padding: 20px;
          border-radius: 14px;
          border: 1px solid #e2e8f0;
        }
        .em-result-stat-success { background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); }
        .em-result-stat-danger  { background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%); }
        .em-result-stat-info    { background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); }
        .em-result-stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: #0f172a;
          line-height: 1;
          letter-spacing: -0.02em;
        }
        .em-result-stat-success .em-result-stat-value { color: #059669; }
        .em-result-stat-danger .em-result-stat-value { color: #dc2626; }
        .em-result-stat-info .em-result-stat-value { color: #2563eb; }
        .em-result-stat-label {
          font-size: 0.72rem;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-top: 8px;
        }
        .em-result-split {
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border-radius: 12px;
          padding: 20px;
          border: 1px solid #bfdbfe;
        }
        .em-result-split-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
          color: #1e40af;
          font-size: 0.9rem;
          margin-bottom: 12px;
        }
        .em-result-split-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 14px;
          background: white;
          border-radius: 8px;
          margin-bottom: 8px;
          font-size: 0.85rem;
          color: #334155;
        }
        .em-result-split-item:last-child {
          margin-bottom: 0;
        }
        .em-result-split-value {
          font-weight: 700;
          color: #0f172a;
        }
        .em-result-split-check {
          color: #10b981;
          font-size: 1.1rem;
        }
        .em-result-error-message {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          padding: 20px;
          background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
          border-radius: 12px;
          color: #991b1b;
          border-left: 4px solid #ef4444;
        }
        .em-result-error-message i {
          font-size: 1.8rem;
          color: #ef4444;
          flex-shrink: 0;
        }
        .em-result-footer {
          padding: 20px 32px;
          background: #fafbfc;
          border-top: 1px solid #f1f5f9;
          display: flex;
          justify-content: center;
          gap: 12px;
        }

        /* ========== ANIMATIONS ========== */
        .spinning {
          animation: em-spin 1s linear infinite;
          display: inline-block;
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 992px) {
          .em-result-stats {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 768px) {
          .em-header {
            padding: 20px;
          }
          .em-header-title {
            font-size: 1.2rem;
          }
          .em-upload-body {
            padding: 20px;
          }
          .em-dropzone {
            padding: 32px 20px;
          }
          .em-dropzone-icon {
            width: 60px;
            height: 60px;
            font-size: 28px;
          }
          .em-actions-row {
            flex-direction: column;
          }
          .em-actions-row > * {
            width: 100%;
          }
          .em-format-row {
            grid-template-columns: 1fr;
          }
          .em-format-header {
            display: none;
          }
          .em-result-footer {
            flex-direction: column;
          }
          .em-result-footer > * {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}